import { 
  collection, 
  doc, 
  setDoc, 
  getDocs, 
  getDoc,
  updateDoc, 
  deleteDoc, 
  Timestamp,
  deleteField
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { RelatedStudentInfo } from '../types/relatedStudent';

export interface ObservationNote {
  id: string;
  content: string;
  relatedStudents?: RelatedStudentInfo[]; // 관련 멤버 정보 배열
  recordedDate: Date; // 날짜 버튼의 날짜 + 현재 시각을 결합한 날짜 시각
  timeTag: string; // 타임 태그 정보 (예: "수 1교시")
  noteType: '일반' | '학습' | '행동'; // 노트 타입
}

export interface ClassMemberWithNotes {
  id: string; // 멤버 ID만 저장
  mainNotes?: string[]; // 메인 노트 배열 (여러 개의 노트)
  observationNotes: ObservationNote[]; // 관찰 노트 (날짜/타임태그 포함)
}

export interface MembersNoteDocument {
  members: Array<{
    id: string; // 멤버 ID만
    mainNotes?: string[]; // 메인 노트 배열
    observationNotes: Array<Omit<ObservationNote, 'recordedDate'> & {
      recordedDate: Timestamp;
    }>;
  }>;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  partitionNumber: number;
}

/** V2: members가 객체일 때 members[memberId].observationNotes는 { [noteId]: note } */
function observationNotesRawToArray(raw: any): ObservationNote[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((n: any) => ({
      ...n,
      recordedDate: n.recordedDate?.toDate?.() ?? (n.recordedDate instanceof Date ? n.recordedDate : new Date((n.recordedDate?.seconds ?? 0) * 1000))
    }));
  }
  if (typeof raw === 'object') {
    const arr = Object.values(raw).map((n: any) => ({
      ...n,
      recordedDate: n.recordedDate?.toDate?.() ?? (n.recordedDate instanceof Date ? n.recordedDate : new Date((n.recordedDate?.seconds ?? 0) * 1000))
    }));
    arr.sort((a: any, b: any) => (a.recordedDate?.getTime?.() ?? 0) - (b.recordedDate?.getTime?.() ?? 0));
    return arr;
  }
  return [];
}

function normalizeNoteForFirestore(note: ObservationNote): Record<string, any> {
  return {
    ...note,
    recordedDate: note.recordedDate instanceof Date ? Timestamp.fromDate(note.recordedDate) : (note.recordedDate as any)
  };
}

export class ClassMembersNotePartitionService {
  private userId: string;
  private classId: string;
  private memberGroup: string;
  private readonly MAX_DOCUMENT_SIZE = 1000000; // 1MB
  private readonly MAX_NOTES_PER_MEMBER = 100; // 멤버당 최대 노트 수

  constructor(userId: string, classId: string, memberGroup?: string) {
    this.userId = userId;
    this.classId = classId;

    // 그룹 파라미터가 없으면(localStorage 기반) 현재 선택된 그룹 사용
    let resolvedGroup = memberGroup;
    if (typeof resolvedGroup === 'undefined') {
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          const key = `selected_member_group_${userId}_${classId}`;
          const saved = window.localStorage.getItem(key);
          if (saved) {
            resolvedGroup = saved;
          }
        }
      } catch {
        // ignore
      }
    }
    this.memberGroup = (resolvedGroup || 'A').toString().trim().toUpperCase();
  }

  private getClassMembersNoteCollectionPath(): string {
    const basePath = `users/${this.userId}/classes/${this.classId}`;
    if (this.memberGroup && this.memberGroup !== 'A') {
      return `${basePath}/ClassMembersNote_${this.memberGroup}`;
    }
    return `${basePath}/ClassMembersNote`;
  }

  private getDocumentId(partitionNumber: number): string {
    if (this.memberGroup === 'A') {
      return `MembersNote-${partitionNumber}`;
    }
    return `MembersNote${this.memberGroup}-${partitionNumber}`;
  }

  private parsePartitionNumber(docId: string): number | null {
    const regex =
      this.memberGroup === 'A'
        ? /^MembersNote-(\d+)$/
        : new RegExp(`^MembersNote${this.memberGroup}-(\\d+)$`);
    const match = docId.match(regex);
    if (!match) return null;
    return parseInt(match[1], 10);
  }

  private isCurrentGroupDocId(docId: string): boolean {
    return this.parsePartitionNumber(docId) !== null;
  }

  /**
   * studentId가 속한 파티션 문서 ID 반환 (없으면 마지막 문서 ID)
   */
  private async getPartitionDocIdForMember(studentId: string): Promise<{ docId: string; exists: boolean }> {
    const collectionRef = collection(db, this.getClassMembersNoteCollectionPath());
    const snapshot = await getDocs(collectionRef);
    const sortedDocs = snapshot.docs
      .filter(d => this.isCurrentGroupDocId(d.id))
      .sort((a, b) => (this.parsePartitionNumber(a.id) || 0) - (this.parsePartitionNumber(b.id) || 0));
    if (sortedDocs.length === 0) {
      return { docId: this.getDocumentId(1), exists: false };
    }
    for (const d of sortedDocs) {
      const data = d.data();
      const members = data.members;
      if (Array.isArray(members)) {
        if (members.some((m: any) => m.id === studentId)) return { docId: d.id, exists: true };
      } else if (members && typeof members === 'object') {
        if (members[studentId]) return { docId: d.id, exists: true };
      }
    }
    return { docId: sortedDocs[sortedDocs.length - 1].id, exists: false };
  }

  /**
   * 모든 파티션 문서 조회
   */
  async loadAllMembers(): Promise<ClassMemberWithNotes[]> {
    try {
      console.log('📚 모든 멤버 노트 데이터 로드 시작');
      
      const collectionRef = collection(db, this.getClassMembersNoteCollectionPath());
      const snapshot = await getDocs(collectionRef);
      
      if (snapshot.empty) {
        console.log('📚 노트 데이터가 없습니다');
        return [];
      }

      const allMembers: ClassMemberWithNotes[] = [];
      
      // 현재 그룹 문서만 필터링 + 파티션 번호 순으로 정렬
      const sortedDocs = snapshot.docs
        .filter(d => this.isCurrentGroupDocId(d.id))
        .sort((a, b) => {
          const aNum = this.parsePartitionNumber(a.id) || 0;
          const bNum = this.parsePartitionNumber(b.id) || 0;
          return aNum - bNum;
        });

      for (const docSnapshot of sortedDocs) {
        const data = docSnapshot.data() as any;
        const membersRaw = data.members || [];

        if (Array.isArray(membersRaw)) {
          const migratedMembers = membersRaw.map((member: any) => {
            return {
              id: member.id,
              mainNotes: member.mainNotes || (member.mainNote ? [member.mainNote] : []),
              observationNotes: observationNotesRawToArray(member.observationNotes).map((note: any) => {
                const migratedNote = { ...note };
                if ((note as any).relatedStudentIds && !(note as any).relatedStudents) {
                  (migratedNote as any).relatedStudents = [];
                }
                return migratedNote;
              })
            };
          });
          allMembers.push(...migratedMembers);
        } else {
          for (const [memberId, member] of Object.entries(membersRaw as Record<string, any>)) {
            const m = member as any;
            const notes = observationNotesRawToArray(m?.observationNotes).map((note: any) => {
              const migratedNote = { ...note };
              if ((note as any).relatedStudentIds && !(note as any).relatedStudents) {
                (migratedNote as any).relatedStudents = [];
              }
              return migratedNote;
            });
            allMembers.push({
              id: memberId,
              mainNotes: m?.mainNotes || [],
              observationNotes: notes
            });
          }
        }
      }

      console.log(`📚 총 ${allMembers.length}명의 멤버 노트 데이터 로드 완료`);
      return allMembers;
    } catch (error) {
      console.error('❌ 멤버 노트 데이터 로드 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 멤버의 노트 데이터 조회
   */
  async getStudentNotes(studentId: string): Promise<ClassMemberWithNotes | null> {
    try {
      const allMembers = await this.loadAllMembers();
      const student = allMembers.find(member => member.id === studentId);
      return student || null;
    } catch (error) {
      console.error('❌ 멤버 노트 조회 실패:', error);
      throw error;
    }
  }

  /**
   * MainNotes 업데이트 (전체 배열)
   */
  async updateMainNotes(studentId: string, mainNotes: string[]): Promise<void> {
    try {
      console.log(`📝 멤버 ${studentId} MainNotes 업데이트 시작`);
      
      const allMembers = await this.loadAllMembers();
      let studentIndex = allMembers.findIndex(member => member.id === studentId);
      
      // 멤버를 찾지 못한 경우, 실제 클래스 멤버인지 확인하고 자동 초기화
      if (studentIndex === -1) {
        console.log(`⚠️ 멤버 ${studentId}가 ClassMembersNote에 없습니다. 실제 멤버인지 확인 중...`);
        
        // ClassMembersPartitionService를 통해 실제 멤버인지 확인
        const { ClassMembersPartitionService } = await import('./classMembersPartitionService');
        const classMembersService = new ClassMembersPartitionService(this.userId, this.classId, this.memberGroup);
        const actualMember = await classMembersService.getMember(studentId);
        
        if (!actualMember) {
          throw new Error(`멤버 ${studentId}을 찾을 수 없습니다 (클래스 멤버에도 존재하지 않음)`);
        }
        
        // 실제 멤버가 존재하므로 ClassMembersNote에 초기화
        console.log(`✅ 멤버 ${studentId}가 실제 클래스 멤버입니다. ClassMembersNote에 초기화 중...`);
        allMembers.push({
          id: studentId,
          mainNotes: [],
          observationNotes: []
        });
        studentIndex = allMembers.length - 1;
      }

      // MainNotes 배열 업데이트
      allMembers[studentIndex].mainNotes = mainNotes;

      // 파티션 재배치
      await this.redistributeMembers(allMembers);
      
      console.log(`✅ 멤버 ${studentId} MainNotes 업데이트 완료`);
    } catch (error) {
      console.error('❌ 멤버 MainNotes 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * ObservationNote 추가 (V2: 단일 문서 dot notation 업데이트)
   */
  async addObservationNote(studentId: string, note: Omit<ObservationNote, 'id'>): Promise<void> {
    try {
      console.log(`📝 멤버 ${studentId} 관찰 노트 추가 시작`);
      const { docId } = await this.getPartitionDocIdForMember(studentId);
      const docRef = doc(db, this.getClassMembersNoteCollectionPath(), docId);
      const docSnap = await getDoc(docRef);

      const newNote: ObservationNote = {
        ...note,
        id: `obs_note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };
      const entryForFirestore = normalizeNoteForFirestore(newNote);

      if (!docSnap.exists()) {
        await setDoc(docRef, {
          members: { [studentId]: { mainNotes: [], observationNotes: { [newNote.id]: entryForFirestore } } },
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          partitionNumber: this.parsePartitionNumber(docId) || 1
        });
        console.log(`✅ 멤버 ${studentId} 관찰 노트 추가 완료 (신규 문서 V2)`);
        return;
      }

      const data = docSnap.data() as any;
      const members = data.members;
      const membersIsObject = members && !Array.isArray(members);
      const memberExists = membersIsObject ? !!members[studentId] : (Array.isArray(members) && members.some((m: any) => m.id === studentId));

      if (membersIsObject) {
        if (memberExists) {
          await updateDoc(docRef, {
            [`members.${studentId}.observationNotes.${newNote.id}`]: entryForFirestore,
            updatedAt: Timestamp.now()
          });
        } else {
          await updateDoc(docRef, {
            [`members.${studentId}`]: { mainNotes: [], observationNotes: { [newNote.id]: entryForFirestore } },
            updatedAt: Timestamp.now()
          });
        }
        console.log(`✅ 멤버 ${studentId} 관찰 노트 추가 완료 (V2 부분 업데이트)`);
        return;
      }

      const allMembers = await this.loadAllMembers();
      let studentIndex = allMembers.findIndex(member => member.id === studentId);
      if (studentIndex === -1) {
        const { ClassMembersPartitionService } = await import('./classMembersPartitionService');
        const classMembersService = new ClassMembersPartitionService(this.userId, this.classId, this.memberGroup);
        const actualMember = await classMembersService.getMember(studentId);
        if (!actualMember) throw new Error(`멤버 ${studentId}을 찾을 수 없습니다 (클래스 멤버에도 존재하지 않음)`);
        allMembers.push({ id: studentId, mainNotes: [], observationNotes: [] });
        studentIndex = allMembers.length - 1;
      }
      if (!allMembers[studentIndex].observationNotes) allMembers[studentIndex].observationNotes = [];
      allMembers[studentIndex].observationNotes = [...allMembers[studentIndex].observationNotes, newNote];
      await this.redistributeMembers(allMembers);
      console.log(`✅ 멤버 ${studentId} 관찰 노트 추가 완료`);
    } catch (error) {
      console.error('❌ 멤버 관찰 노트 추가 실패:', error);
      throw error;
    }
  }


  /**
   * 파티션 재배치 (용량 제한 고려)
   */
  private async redistributeMembers(allMembers: ClassMemberWithNotes[]): Promise<void> {
    try {
      console.log('🔄 노트 데이터 파티션 재배치 시작');
      
      // 기존 파티션 문서들 삭제
      const collectionRef = collection(db, this.getClassMembersNoteCollectionPath());
      const snapshot = await getDocs(collectionRef);
      
      for (const docSnapshot of snapshot.docs) {
        // 현재 그룹 문서만 삭제 (다른 그룹 보호)
        if (!this.isCurrentGroupDocId(docSnapshot.id)) continue;
        await deleteDoc(docSnapshot.ref);
      }

      // 새 파티션으로 재배치
      let currentPartition = 1;
      let currentMembers: ClassMemberWithNotes[] = [];
      let currentSize = 0;

      for (const member of allMembers) {
        const memberSize = this.estimateMemberSize(member);
        
        if (currentSize + memberSize > this.MAX_DOCUMENT_SIZE && currentMembers.length > 0) {
          // 현재 파티션 저장
          await this.savePartition(currentPartition, currentMembers);
          
          // 새 파티션 시작
          currentPartition++;
          currentMembers = [member];
          currentSize = memberSize;
        } else {
          currentMembers.push(member);
          currentSize += memberSize;
        }
      }

      // 마지막 파티션 저장
      if (currentMembers.length > 0) {
        await this.savePartition(currentPartition, currentMembers);
      }

      console.log(`✅ 노트 데이터 파티션 재배치 완료 (${currentPartition}개 파티션)`);
    } catch (error) {
      console.error('❌ 노트 데이터 파티션 재배치 실패:', error);
      throw error;
    }
  }

  /**
   * 파티션 저장 (V2: members·observationNotes 객체 형식)
   */
  private async savePartition(partitionNumber: number, members: ClassMemberWithNotes[]): Promise<void> {
    const docRef = doc(db, this.getClassMembersNoteCollectionPath(), this.getDocumentId(partitionNumber));
    const membersObj: Record<string, any> = {};
    for (const member of members) {
      const notesObj: Record<string, any> = {};
      for (const note of member.observationNotes || []) {
        notesObj[note.id] = normalizeNoteForFirestore(note);
      }
      membersObj[member.id] = {
        mainNotes: member.mainNotes || [],
        observationNotes: notesObj
      };
    }
    const membersNoteDoc = {
      members: membersObj,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      partitionNumber
    };
    await setDoc(docRef, membersNoteDoc);
    console.log(`📚 파티션 ${partitionNumber} 저장 완료 (${members.length}명, V2)`);
  }

  /**
   * 멤버 데이터 크기 추정 (바이트)
   */
  private estimateMemberSize(member: ClassMemberWithNotes): number {
    const observationNotesSize = (member.observationNotes || []).reduce((total, note) => {
      return total + JSON.stringify(note).length * 2;
    }, 0);
    
    const memberSize = JSON.stringify(member).length * 2;
    return memberSize + observationNotesSize;
  }

  /**
   * 클래스 노트 데이터 초기화
   */
  async initializeClassNotes(classMembers: Array<{ id: string }>): Promise<void> {
    try {
      console.log('📚 클래스 노트 데이터 초기화 시작');
      
      // 멤버 ID와 빈 배열들만 저장
      const membersWithNotes: ClassMemberWithNotes[] = classMembers.map(member => ({
        id: member.id,
        mainNotes: [],
        observationNotes: []
      }));

      await this.redistributeMembers(membersWithNotes);
      
      console.log('✅ 클래스 노트 데이터 초기화 완료');
    } catch (error) {
      console.error('❌ 클래스 노트 데이터 초기화 실패:', error);
      throw error;
    }
  }

  // 관찰 기록 수정 (V2: 단일 문서 dot notation)
  async updateObservationNote(studentId: string, noteId: string, updatedNote: ObservationNote): Promise<void> {
    try {
      const { docId } = await this.getPartitionDocIdForMember(studentId);
      const docRef = doc(db, this.getClassMembersNoteCollectionPath(), docId);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error(`멤버 ${studentId}를 찾을 수 없습니다.`);
      const data = docSnap.data() as any;
      const members = data.members;
      if (members && !Array.isArray(members) && members[studentId]) {
        const entry = normalizeNoteForFirestore(updatedNote);
        await updateDoc(docRef, {
          [`members.${studentId}.observationNotes.${noteId}`]: entry,
          updatedAt: Timestamp.now()
        });
        console.log(`✅ 멤버 ${studentId} 관찰 기록 ${noteId} 수정 완료 (V2)`);
        return;
      }
      const allMembers = await this.loadAllMembers();
      const memberIndex = allMembers.findIndex(member => member.id === studentId);
      if (memberIndex === -1) throw new Error(`멤버 ${studentId}를 찾을 수 없습니다.`);
      const noteIndex = allMembers[memberIndex].observationNotes.findIndex(note => note.id === noteId);
      if (noteIndex === -1) throw new Error(`관찰 기록 ${noteId}를 찾을 수 없습니다.`);
      allMembers[memberIndex].observationNotes[noteIndex] = { ...updatedNote, recordedDate: updatedNote.recordedDate } as any;
      await this.redistributeMembers(allMembers);
      console.log(`✅ 멤버 ${studentId} 관찰 기록 ${noteId} 수정 완료`);
    } catch (error) {
      console.error('❌ 멤버 관찰 기록 수정 실패:', error);
      throw error;
    }
  }

  // 관찰 기록 삭제 (V2: deleteField)
  async deleteObservationNote(studentId: string, noteId: string): Promise<void> {
    try {
      const { docId } = await this.getPartitionDocIdForMember(studentId);
      const docRef = doc(db, this.getClassMembersNoteCollectionPath(), docId);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error(`멤버 ${studentId}를 찾을 수 없습니다.`);
      const data = docSnap.data() as any;
      const members = data.members;
      if (members && !Array.isArray(members) && members[studentId]) {
        await updateDoc(docRef, {
          [`members.${studentId}.observationNotes.${noteId}`]: deleteField(),
          updatedAt: Timestamp.now()
        });
        console.log(`✅ 멤버 ${studentId} 관찰 기록 ${noteId} 삭제 완료 (V2)`);
        return;
      }
      const allMembers = await this.loadAllMembers();
      const memberIndex = allMembers.findIndex(member => member.id === studentId);
      if (memberIndex === -1) throw new Error(`멤버 ${studentId}를 찾을 수 없습니다.`);
      allMembers[memberIndex].observationNotes = allMembers[memberIndex].observationNotes.filter(note => note.id !== noteId);
      await this.redistributeMembers(allMembers);
      console.log(`✅ 멤버 ${studentId} 관찰 기록 ${noteId} 삭제 완료`);
    } catch (error) {
      console.error('❌ 멤버 관찰 기록 삭제 실패:', error);
      throw error;
    }
  }
}
